/**
 * Long-term identity, prekey generation and publication.
 */
import {
  type KeyPair,
  generateDHKeyPair,
  generateIdentityKeyPair,
  sign,
  verify,
  toB64,
  fromB64,
  utf8,
  sha256,
  concatBytes,
  randomBytes,
  timingSafeEqual,
  kdf,
} from './primitives.ts';
import type { OneTimePreKeyPublic, PreKeyBundle, SignedPreKeyPublic } from '../types.ts';

/** Domain separator so a prekey signature can never be replayed as a login. */
export const SPK_SIG_CONTEXT = 'VAV-SignedPreKey-v1';

export interface StoredPreKey {
  id: number;
  keys: KeyPair;
  createdAt: number;
}

export interface StoredSignedPreKey extends StoredPreKey {
  sig: Uint8Array;
}

/** The complete secret state of one device. Persisted locally, never uploaded. */
export interface IdentityState {
  userId: string;
  deviceId: number;
  registrationId: number;
  identity: KeyPair;
  signedPreKey: StoredSignedPreKey;
  /** Previous signed prekey, retained so in-flight handshakes still resolve. */
  previousSignedPreKey?: StoredSignedPreKey;
  oneTimePreKeys: Record<number, KeyPair>;
  nextPreKeyId: number;
}

function spkSignedBytes(id: number, pub: Uint8Array): Uint8Array {
  // The id is bound into the signature so a server cannot re-advertise an old
  // signed prekey under a new id to steer senders at a key it has ground down.
  const idBytes = new Uint8Array(4);
  new DataView(idBytes.buffer).setUint32(0, id, false);
  return concatBytes(utf8(SPK_SIG_CONTEXT), idBytes, pub);
}

export function createSignedPreKey(identity: KeyPair, id: number): StoredSignedPreKey {
  const keys = generateDHKeyPair();
  return { id, keys, sig: sign(identity.priv, spkSignedBytes(id, keys.pub)), createdAt: Date.now() };
}

export function createOneTimePreKeys(startId: number, count: number): StoredPreKey[] {
  const out: StoredPreKey[] = [];
  for (let i = 0; i < count; i++) {
    out.push({ id: startId + i, keys: generateDHKeyPair(), createdAt: Date.now() });
  }
  return out;
}

export const DEFAULT_PREKEY_COUNT = 100;

/**
 * Create a brand-new device identity. `userId` is assigned by the server at
 * registration; until then callers pass a placeholder and fill it in after.
 */
export function createIdentity(
  userId: string,
  deviceId = 1,
  preKeyCount = DEFAULT_PREKEY_COUNT,
): IdentityState {
  const identity = generateIdentityKeyPair();
  const rid = randomBytes(2);
  const signedPreKey = createSignedPreKey(identity, 1);
  const oneTimePreKeys: Record<number, KeyPair> = {};
  for (const pk of createOneTimePreKeys(1, preKeyCount)) oneTimePreKeys[pk.id] = pk.keys;
  return {
    userId,
    deviceId,
    // 14-bit id, matching Signal's range; used to detect a reinstalled device.
    registrationId: ((rid[0] << 8) | rid[1]) & 0x3fff,
    identity,
    signedPreKey,
    oneTimePreKeys,
    nextPreKeyId: preKeyCount + 1,
  };
}

/** Public half of the signed prekey, for upload. */
export function publicSignedPreKey(spk: StoredSignedPreKey): SignedPreKeyPublic {
  return { id: spk.id, pub: toB64(spk.keys.pub), sig: toB64(spk.sig), createdAt: spk.createdAt };
}

export function publicOneTimePreKeys(state: IdentityState): OneTimePreKeyPublic[] {
  return Object.entries(state.oneTimePreKeys).map(([id, keys]) => ({
    id: Number(id),
    pub: toB64(keys.pub),
  }));
}

/**
 * Rotate the signed prekey. The outgoing key is kept as `previousSignedPreKey`
 * so senders who fetched the old bundle moments ago can still complete their
 * handshake instead of getting an undecryptable message.
 */
export function rotateSignedPreKey(state: IdentityState): IdentityState {
  const next = createSignedPreKey(state.identity, state.signedPreKey.id + 1);
  return { ...state, previousSignedPreKey: state.signedPreKey, signedPreKey: next };
}

/** Top up one-time prekeys when the server reports the pool running low. */
export function replenishOneTimePreKeys(
  state: IdentityState,
  count: number,
): { state: IdentityState; added: OneTimePreKeyPublic[] } {
  const fresh = createOneTimePreKeys(state.nextPreKeyId, count);
  const oneTimePreKeys = { ...state.oneTimePreKeys };
  for (const pk of fresh) oneTimePreKeys[pk.id] = pk.keys;
  return {
    state: { ...state, oneTimePreKeys, nextPreKeyId: state.nextPreKeyId + count },
    added: fresh.map((pk) => ({ id: pk.id, pub: toB64(pk.keys.pub) })),
  };
}

/**
 * Verify a bundle fetched from the server before using it.
 *
 * This is the single check that keeps a malicious server from handing us its
 * own prekey: the signature must come from the identity key we are about to
 * display to the user as their contact.
 */
export function verifyPreKeyBundle(bundle: PreKeyBundle): boolean {
  const identityKey = fromB64(bundle.identityKey);
  if (identityKey.length !== 32) return false;
  const spkPub = fromB64(bundle.signedPreKey.pub);
  if (spkPub.length !== 32) return false;
  return verify(identityKey, spkSignedBytes(bundle.signedPreKey.id, spkPub), fromB64(bundle.signedPreKey.sig));
}

// ---------------------------------------------------------------------------
// Safety numbers
// ---------------------------------------------------------------------------

/**
 * A 60-digit safety number, computed identically on both sides, that users can
 * compare out of band to rule out a man-in-the-middle. Sorting the two identity
 * keys makes the result order-independent.
 */
export function safetyNumber(myIdentity: Uint8Array, theirIdentity: Uint8Array): string {
  const a = toB64(myIdentity);
  const b = toB64(theirIdentity);
  const [first, second] = a < b ? [myIdentity, theirIdentity] : [theirIdentity, myIdentity];
  const digest = fingerprintDigest(concatBytes(first, second));
  let out = '';
  // 5 digits per 40-bit chunk, 12 chunks = 60 digits.
  for (let i = 0; i < 12; i++) {
    let value = 0;
    for (let j = 0; j < 5; j++) value = value * 256 + digest[i * 5 + j];
    out += (value % 100000).toString().padStart(5, '0');
    if (i % 4 === 3 && i !== 11) out += '\n';
    else if (i !== 11) out += ' ';
  }
  return out;
}

/**
 * 5200 iterations of SHA-256, matching Signal's fingerprint hardening, then
 * expanded to the 60 bytes the digit encoding consumes. SHA-256 only yields 32,
 * so the expansion step is what keeps the last seven chunks from reading past
 * the end of the digest.
 */
const FINGERPRINT_BYTES = 60;

function fingerprintDigest(input: Uint8Array): Uint8Array {
  let h = concatBytes(utf8('VAV-Fingerprint-v1'), input);
  for (let i = 0; i < 5200; i++) h = sha256(h);
  return kdf(h, input, 'VAV-Fingerprint-Expand-v1', FINGERPRINT_BYTES);
}

export function safetyNumbersMatch(a: string, b: string): boolean {
  const norm = (s: string) => utf8(s.replace(/\s+/g, ''));
  return timingSafeEqual(norm(a), norm(b));
}
